import { focusManager } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { lazy, type PropsWithChildren, Suspense, useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { AuthProvider } from '@/features/auth';
import { registerEntryWorkspaceSaveMutationDefaults } from '@/features/shiftbook/hooks/use-entry-workspace-save';
import { ThemeProvider } from '@/features/shiftbook/theme/theme-provider';
import { setupFocusManager, setupOfflineMutationReplay } from '@/lib/offline/sync';
import {
  bindOnlineManagerToNavigator,
  persistOptions,
  queryClient,
  removeOrphanedPendingMutations,
} from '@/lib/query-client';
import {
  isQueryDevtoolsEnabled,
  subscribeToQueryDevtoolsPreference,
} from '@/lib/query-devtools-preference';
import { AppToastProvider } from '@/lib/toast';
import { registerServiceWorker } from '@/pwa/register-sw';
import type { router } from './router';
import { WorkspaceReplayToastBridge } from './WorkspaceReplayToastBridge';

type ProvidersProps = PropsWithChildren<{ router: typeof router }>;

// Must run before PersistQueryClientProvider restores and resumes paused
// mutations: queued offline saves replay through these defaults when no
// dialog is mounted yet (AG-10).
registerEntryWorkspaceSaveMutationDefaults(queryClient);

const Devtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((module) => ({
        default: module.ReactQueryDevtools,
      })),
    )
  : null;

export function AppProviders({ router }: ProvidersProps) {
  const [showQueryDevtools, setShowQueryDevtools] = useState(() => isQueryDevtoolsEnabled());

  useEffect(() => {
    bindOnlineManagerToNavigator();
    setupFocusManager();
    const cleanup = setupOfflineMutationReplay(queryClient);
    const cleanupServiceWorker = registerServiceWorker();

    // keep focus manager aware of page visibility for stale-while-revalidate
    focusManager.setFocused(!document.hidden);

    return () => {
      cleanupServiceWorker?.();
      cleanup?.();
    };
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    setShowQueryDevtools(isQueryDevtoolsEnabled());

    return subscribeToQueryDevtoolsPreference(() => {
      setShowQueryDevtools(isQueryDevtoolsEnabled());
    });
  }, []);

  return (
    <ThemeProvider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
        onSuccess={() => {
          removeOrphanedPendingMutations(queryClient);
        }}
      >
        <AppToastProvider>
          <WorkspaceReplayToastBridge />
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
          {Devtools && showQueryDevtools ? (
            <Suspense fallback={null}>
              <Devtools initialIsOpen={false} buttonPosition="bottom-left" />
            </Suspense>
          ) : null}
        </AppToastProvider>
      </PersistQueryClientProvider>
    </ThemeProvider>
  );
}
